import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlarmClock } from "lucide-react";
import { toast } from "sonner";
import { useDirtyGuard } from "../../context/DirtyGuard";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { AttendancePolicyFields, minCheckoutGrace } from "../admin/branch/AttendancePolicyFields";
import { myCompany, updateCompany } from "../../api/directory";
import { clearCompanyCache } from "../../api/useMyCompany";

const DEFAULTS = {
  daily_grace_minutes: 0,
  monthly_extra_late_allowance: 0,
  extra_late_minutes: 0,
  checkout_grace_minutes: 30,
};

// Company-wide lateness rules inherited by every branch.
export const CompanyAttendancePolicy = () => {
  const { t } = useTranslation();
  const [policy, setPolicy] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    myCompany()
      .then((data) => {
        const merged = { ...DEFAULTS, ...(data.attendance_policy || {}) };
        setPolicy(merged);
        setSaved(merged);
      })
      .catch(() => setPolicy(null));
  }, []);

  const save = useCallback(async () => {
    const minimum = minCheckoutGrace(policy);
    if (Number(policy.checkout_grace_minutes || 0) < minimum) {
      toast.error(t("branches.checkoutGraceMin", { minutes: minimum }));
      throw new Error("invalid_policy");
    }
    setBusy(true);
    try {
      await updateCompany({ attendance_policy: policy });
      clearCompanyCache();
      setSaved(policy);
      toast.success(t("company.saved"));
    } catch {
      toast.error(t("errors.generic"));
      throw new Error("save_failed");
    } finally {
      setBusy(false);
    }
  }, [policy, t]);

  useDirtyGuard(Boolean(policy && saved && JSON.stringify(policy) !== JSON.stringify(saved)), save);

  if (!policy) return null;

  return (
    <NeuCard data-testid="company-attendance-policy">
      <p className="label-xs flex items-center gap-2">
        <AlarmClock className="h-4 w-4 text-primary" /> {t("branches.attendancePolicy")}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{t("branches.attendancePolicyHint")}</p>

      <div className="mt-5">
        <AttendancePolicyFields value={policy} onChange={(key, v) => setPolicy({ ...policy, [key]: v })} />
      </div>

      <NeuButton
        variant="primary"
        className="mt-5"
        data-testid="company-attendance-save"
        disabled={busy}
        onClick={() => save().catch(() => {})}
      >
        {t("app.save")}
      </NeuButton>
    </NeuCard>
  );
};
